import { useEffect, useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { generatePath } from 'react-router-dom';
import { deserialize } from 'serializr';
import { ProcessingFacility as ProcessingFacilityModel } from 'models/ProcessingFacility/ProcessingFacility.model';
import { ValueChainServiceProps } from 'enums/ValueChainServiceEnums';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/NotificationEnums';
import { PFApiMessages } from 'shared/components/ValueChain/ProcessingFacility/pfsuccessmessages';

const ValueChainService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [processingFacilityList, setProcessingFacilityList] = useState<
    ProcessingFacilityModel[]
  >([]);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  /** Fetch Processing facility */
  const fetchProcessingFacilities = async (params?: {
    page?: number;
    size?: number;
    name?: string;
    product_id?: number;
    from_value_chain?: boolean;
  }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        ApiRoutes.FETCH_PROCESSING_FACILITY,
        { params }
      );
      let data = deserialize(
        ProcessingFacilityModel,
        response.data['data']
      ) as ProcessingFacilityModel[];
      data = data.map(item => {
        if (item.scope_fields) {
          item.scope_fields = JSON.parse(item.scope_fields);
        }
        return item;
      });
      setProcessingFacilityList(data);
      setTotalPages(response.data[ValueChainServiceProps.TOTAL_PAGES]);
      setTotalItems(response.data[ValueChainServiceProps.TOTAL_ITEMS]);
      setCurrentPage(response.data[ValueChainServiceProps.CURRENT_PAGE]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** create ProcessingFacility */
  const createProcessingFacility = async (ProcessingFacility: {
    name: string | undefined;
    company_id: number;
    scope1: number;
    scope1_unit: string | undefined;
    scope2: number;
    scope2_unit: string | undefined;
    address: {
      street: string | undefined;
      city: string | undefined;
      province: string | undefined;
      country: string | undefined;
      postal_code: string | undefined;
      lat?: number;
      long?: number;
    };
  }) => {
    try {
      setButtonLoading(true);
      const res = await axiosInstance.post(
        ApiRoutes.CREATE_PROCESSING_FACILITY,
        ProcessingFacility
      );
      const updatedArr = [
        ...processingFacilityList,
        res.data.data.processingFacility as ProcessingFacilityModel,
      ];
      await fetchProcessingFacilities({ size: 20 });
      return res.data.data.processingFacility as ProcessingFacilityModel;
    } catch (error) {
      setButtonLoading(false);
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  /* Handle Processing Facility delete */
  const deleteFacility = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.DELETE_PROCESSING_FACILITY, { id });
      await axiosInstance.delete(url);
      const updateArr = processingFacilityList.filter(
        element => element.id !== id
      );
      setProcessingFacilityList(updateArr);
      Notification({
        message: PFApiMessages.PF_DELETED,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Handle Update Processing Facility */
  const updateProcessingFacility = async (
    id: number | undefined,
    ProcessingFacility: {
      name: string | undefined;
      company_id: number;
      scope1: number;
      scope1_unit: string | undefined;
      scope2: number;
      scope2_unit: string | undefined;
      address: {
        street: string | undefined;
        city: string | undefined;
        province: string | undefined;
        country: string | undefined;
        postal_code: string | undefined;
        lat?: number;
        long?: number;
      };
    }
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.UPDATE_PROCESSING_FACILITY, { id });
      await axiosInstance.put(url, ProcessingFacility);
      await fetchProcessingFacilities({ size: 20 });
      Notification({
        message: PFApiMessages.PF_UPDATED,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  const ScopeCalculation = async (payload: {
    natural_gas: number | undefined;
    natural_gas_unit: string | undefined;
    diesel: number | undefined;
    gasoline: number | undefined;
    wood: number | undefined;
    purchased_co2: number | undefined;
    refrigerant: number | undefined;
    refrigerant_label: string | undefined;
    heating_oil: number | undefined;
    propane: number | undefined;
    electricity: number | undefined;
    electricity_region_spec_id: number | undefined;
    refrigerant_details: {
      refrigerant: string;
      refrigerant_region_spec_id: string;
      refrigerant_label: string;
    }[];
  }) => {
    try {
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.SCOPE_CALCULATOR, payload);
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  useEffect(() => {
    setLoading(false);
    setButtonLoading(false);
    return () => {
      setLoading(false);
      setButtonLoading(false);
    };
  }, []);

  return {
    processingFacilityList,
    loading,
    pageSize,
    totalItems,
    totalPages,
    currentPage,
    buttonLoading,
    fetchProcessingFacilities,
    setPageSize,
    createProcessingFacility,
    deleteFacility,
    updateProcessingFacility,
    ScopeCalculation,
  };
};

export default ValueChainService;
